import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

/**
 * EditorRico — wrapper de ReactQuill configurado para edición de plantillas
 * de email.
 *
 * Toolbar limitado a opciones que sobreviven en clientes de email:
 *   - Cabeceras (H1, H2, H3)
 *   - Negrita, cursiva, subrayado
 *   - Color de texto y fondo
 *   - Listas ordenadas y desordenadas
 *   - Alineación
 *   - Enlaces
 *   - Quitar formato
 *
 * Expone vía ref un método `insertarEnCursor(texto)` para que el padre
 * pueda insertar variables tipo {{usuario_nombre}} en la posición donde
 * está el cursor.
 *
 * NOTA: react-quill-new es el fork mantenido de react-quill que funciona
 * con React 18+. Si por algún motivo prefieres react-quill@2.0.0-beta.4,
 * cambia el import — la API es idéntica.
 */

const TOOLBAR_EMAIL = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }, { background: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link'],
  ['clean'],
];

const FORMATS_EMAIL = [
  'header',
  'bold', 'italic', 'underline',
  'color', 'background',
  'list', 'bullet', 'indent',
  'align',
  'link',
];

/**
 * AMPLIACIÓN (Credenciales, 14/09/2026), ADITIVA: `formatosExtra` y `insertarHtmlEnCursor`.
 *
 * Los valores por defecto dejan el comportamiento de Intervenciones EXACTAMENTE como estaba —
 * misma barra, mismos formatos—. Incorporaciones necesita además `image`, porque inserta la
 * miniatura de un vídeo de Vimeo: sin `image` en `formats`, Quill BORRA el <img> al pegarlo.
 *
 * Se pasa por prop en vez de añadirlo a la lista común a propósito: permitir imágenes en las
 * plantillas de correo de Intervenciones es una decisión de aquel módulo, no de éste.
 */
const EditorRico = forwardRef(function EditorRico(
  { value, onChange, placeholder, minHeight = 280, formatosExtra = [] },
  ref
) {
  const quillRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertarEnCursor(texto) {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) return;
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, texto, 'user');
      editor.setSelection(index + texto.length, 0);
      editor.focus();
    },
    /** Inserta HTML ya compuesto en la posición del cursor (o al final si no hay selección). */
    insertarHtmlEnCursor(html) {
      const editor = quillRef.current?.getEditor?.();
      if (!editor) return;
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.clipboard.dangerouslyPasteHTML(index, html, 'user');
      editor.setSelection(editor.getLength(), 0);
      editor.focus();
    },
    focus() {
      quillRef.current?.getEditor?.()?.focus();
    },
  }));

  return (
    <div className="editor-rico" style={{ minHeight }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        modules={{ toolbar: TOOLBAR_EMAIL }}
        formats={formatosExtra.length ? [...FORMATS_EMAIL, ...formatosExtra] : FORMATS_EMAIL}
      />
      <style>{`
        .editor-rico .ql-toolbar {
          border-color: rgb(226 232 240);
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          background: rgb(248 250 252);
        }
        .editor-rico .ql-container {
          border-color: rgb(226 232 240);
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
          font-size: 13px;
          min-height: ${minHeight - 42}px;
        }
        .editor-rico .ql-editor {
          min-height: ${minHeight - 42}px;
        }
        .editor-rico .ql-snow .ql-stroke {
          stroke: rgb(71 85 105);
        }
        .editor-rico .ql-snow .ql-fill {
          fill: rgb(71 85 105);
        }
        .editor-rico .ql-snow .ql-picker {
          color: rgb(71 85 105);
        }
        .editor-rico .ql-snow .ql-active .ql-stroke {
          stroke: #003c66;
        }
        .editor-rico .ql-snow .ql-active .ql-fill {
          fill: #003c66;
        }
      `}</style>
    </div>
  );
});

export default EditorRico;
